import React, { useEffect, useState, useCallback } from "react";
import { Alert, Button } from "@mui/material";
import CampaignIcon from "@mui/icons-material/Campaign";
import { useAuthStore } from "../store";
import StoreModal from "./StoreModal";
import LoginModal from "./Login";
import "../assets/style/PromoBanner.css";

const API = process.env.REACT_APP_BACKEND_ADDRESS;

// Persist dismissal for the browsing session only, so the banner re-appears on
// the user's next visit but does not nag within a single session.
const DISMISS_KEY = "promoBannerDismissed";

/**
 * Public, marketing-oriented banner for the currently promoted pass (karnet).
 *
 * Fetches the active promoted passes from `GET /pass-type/promoted` (no auth)
 * and surfaces the first one as a fixed info bar pinned just below the site
 * header — and, when present, below the `PendingSignupsBanner`. The call to
 * action takes the user straight to buying that pass: it opens the purchase
 * store (`StoreModal`) pre-selected to the promoted pass so checkout is one
 * click away. A logged-out visitor is first shown the login modal (buying
 * requires auth), and the store opens automatically once they sign in.
 *
 * Renders nothing when there is no active promotion or the user dismissed it
 * for this session.
 */
const PromoBanner = () => {
  const { isAuthenticated } = useAuthStore((state) => ({
    isAuthenticated: state.isAuthenticated,
  }));

  const [promo, setPromo] = useState(null);
  const [topOffset, setTopOffset] = useState(0);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(DISMISS_KEY) === "1"
  );
  const [storeOpen, setStoreOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  // Set when a logged-out visitor clicks buy: open the store as soon as auth
  // completes so signing in flows straight into checkout.
  const [openStoreAfterLogin, setOpenStoreAfterLogin] = useState(false);

  // Load the active promotion (first promoted pass wins).
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API}/pass-type/promoted`);
        if (!res.ok) return;
        const data = await res.json();
        const first = (data.objects || [])[0] || null;
        if (!cancelled) setPromo(first);
      } catch (e) {
        // Marketing banner is non-critical: fail silently.
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Pin the banner below the fixed header and the (optional) pending-signups
  // banner, tracking their heights across breakpoints and dynamic mounts.
  const measure = useCallback(() => {
    const header = document.getElementById("header");
    const pending = document.querySelector(".pending-signups-banner");
    let offset = header ? header.offsetHeight : 0;
    if (pending) offset += pending.offsetHeight;
    setTopOffset(offset);
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    // The pending-signups banner mounts asynchronously after its data loads;
    // observe the DOM so we re-pin underneath it when it appears/disappears.
    const observer = new MutationObserver(measure);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      window.removeEventListener("resize", measure);
      observer.disconnect();
    };
  }, [measure]);

  // A logged-out visitor who clicked buy is sent through the login modal; once
  // they are authenticated, open the store straight away so they can complete
  // the purchase without finding the banner again.
  useEffect(() => {
    if (isAuthenticated && openStoreAfterLogin) {
      setOpenStoreAfterLogin(false);
      setLoginOpen(false);
      setStoreOpen(true);
    }
  }, [isAuthenticated, openStoreAfterLogin]);

  if (dismissed || !promo) {
    return null;
  }

  const message =
    promo.promo_text && promo.promo_text.trim().length > 0
      ? promo.promo_text
      : `Promocja: ${promo.name} — ${promo.price} zł`;

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  // Take the user straight to buying the promoted pass. Logged in -> open the
  // store pre-selected to this pass; logged out -> prompt login first, then the
  // store opens automatically (see the effect above).
  const handleBuy = () => {
    if (isAuthenticated) {
      setStoreOpen(true);
    } else {
      setOpenStoreAfterLogin(true);
      setLoginOpen(true);
    }
  };

  // Closing/cancelling the login modal must not leave a pending "open store
  // after login" intent, otherwise a later login elsewhere would unexpectedly
  // pop the store open.
  const handleLoginClose = () => {
    setLoginOpen(false);
    setOpenStoreAfterLogin(false);
  };

  return (
    <>
      <div className="promo-banner" style={{ top: `${topOffset}px` }}>
        <Alert
          severity="info"
          icon={<CampaignIcon />}
          className="promo-banner__alert"
          onClose={handleDismiss}
          action={
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              className="promo-banner__button"
              onClick={handleBuy}
            >
              Kup {promo.name}
            </Button>
          }
        >
          {message}
        </Alert>
      </div>

      <StoreModal
        open={storeOpen}
        onClose={() => setStoreOpen(false)}
        preselectedPassTypeId={promo.id}
      />
      <LoginModal
        open={loginOpen}
        handleClose={handleLoginClose}
        showAlert={() => {}}
      />
    </>
  );
};

export default PromoBanner;
